import React, { useEffect, useState, Fragment } from 'react';
import { Grid, Button, Box } from '@mui/material';
import { useFormik } from 'formik';
import { validationschat, valuesChat } from './formikData';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Checkbox, FormControlLabel } from '@mui/material';
import { IFormChat } from '../../../shared/types/chat';
import { AuthMessages } from '../../../shared/messages/authMessages';
import FieldText from '../../components/general/fields/fieldText';
import AutocompleteField from '../../components/general/fields/autocompleteField';
import { ToastSwal } from '../../components/general/toastSwals/toastSwal';
import { ISelect, ParamEnum } from '../../../shared/types/shared';
import {
    asyncGetParamByCode,
    asyncGetParams,
} from '../../../services/param.service';
import { formatParams } from '../../../shared/utils/params';
import { ModalTermAndConditions } from './modalTermAndCondition';
import { ContainerWebChat } from '../../components/general/container';

export const ChatWebPage = () => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [identifications, setIdentifications] = useState<ISelect[]>([]);
    const [NIT, setNIT] = useState<ISelect>();
    const [contacts, setContacts] = useState<ISelect[]>([]);
    const [open, setOpen] = useState(false);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const getReCaptcha = async () => {
        try {
            if (executeRecaptcha) {
                const localToken = await executeRecaptcha('someAction');
                return localToken;
            }
        } catch (error) {
            console.error('Error al obtener reCAPTCHA:', error);
            return null;
        }
    };

    const iniciarChat = (values: IFormChat) => {
        document.getElementById('chat-client-v4')?.remove();
        document.getElementById('chat-client-v4-script')?.remove();
        document.getElementById('chat-client-v4-script2')?.remove();

        const link = document.createElement('link');
        link.setAttribute('type', 'text/css');
        link.setAttribute('rel', 'stylesheet');
        link.setAttribute('href', process.env.REACT_APP_CHAT_APP_CSS || '');
        link.setAttribute('id', 'chat-client-v4');

        const chatConfig = {
            appId: process.env.REACT_APP_CHAT_APP_ID_WEB,
            apiUrl: process.env.REACT_APP_CHAT_API_URL_WEB,
            tenantUrl: process.env.REACT_APP_CHAT_TENANT_URL_WEB,
            chatPath: process.env.REACT_APP_CHAT_PATH_WEB,
            first_name: values.name,
            last_name: values.surname,
            email: values.email,
            phone_number: values.phone,
            parameters: {
                tipoId: values.type_identification,
                numeroId: values.identification,
                tipoContacto: values.type_contact,
            },
            autostartChat: true,
        };

        const script = document.createElement('script');
        script.setAttribute('type', 'text/javascript');
        script.setAttribute('id', 'chat-client-v4-script');
        script.innerHTML = `SERVICE_PATTERN_CHAT_CONFIG = ${JSON.stringify(
            chatConfig
        )};`;

        const scriptSrc = document.createElement('script');
        scriptSrc.setAttribute('id', 'chat-client-v4-script2');
        scriptSrc.setAttribute(
            'src',
            process.env.REACT_APP_CHAT_APP_SCRIPT || ''
        );

        document.body.appendChild(link);
        document.body.appendChild(script);
        document.body.appendChild(scriptSrc);
    };

    const isFormValid = () => {
        return (
            formik.dirty &&
            formik.isValid &&
            Object.keys(formik.errors).length === 0
        );
    };

    const formik = useFormik({
        initialValues: valuesChat,
        validationSchema: validationschat(NIT?.id || ''),
        onSubmit: async (formChat) => {
            const captcha = (await getReCaptcha()) || '';
            if (!captcha) {
                ToastSwal.fire({
                    text: AuthMessages.login['AUTH-NO-CAPTCHA'],
                    icon: 'error',
                    iconColor: '#1d1d1d',
                });
            } else {
                iniciarChat(formChat);
            }
        },
    });

    const getIdentifications = async () => {
        const items = await asyncGetParams(ParamEnum.DOCUMENT_TYPE);
        setIdentifications(
            items.map(({ code, name }: any) => ({ id: code, name }))
        );
        setNIT(await asyncGetParamByCode(`NIT`));
    };

    const getContacts = async () => {
        const items = await asyncGetParams(ParamEnum.CONTACT_TYPE);
        setContacts(items.map(({ code, name }: any) => ({ id: code, name })));
    };

    useEffect(() => {
        getIdentifications();
        getContacts();
    }, []);

    useEffect(() => {
        const applyChatStyles = (iframe: HTMLIFrameElement) => {
            const style = iframe.style;
            style.position = 'fixed';
            style.border = 'none';
            style.right = '10px';
            style.left = 'auto';
            style.bottom = '10px';

            if (window.innerWidth <= 768) {
                style.width = '90vw';
                style.height = '90vh';
                style.borderRadius = '12px';
            } else {
                style.width = '450px';
                style.height = '90vh';
                style.borderRadius = '8px';
            }
        };

        const alignWrapperRight = () => {
            const w = document.getElementById(
                'sp-min-tab-wrapper'
            ) as HTMLElement | null;
            if (w) {
                w.style.right = '10px';
                w.style.left = 'auto';
                w.style.bottom = '10px';
                w.style.zIndex = '2147483647';
            }
        };

        const checkInterval = setInterval(() => {
            const iframe = document.getElementById(
                'sp-chat-frame'
            ) as HTMLIFrameElement;
            if (iframe) {
                applyChatStyles(iframe);
                alignWrapperRight();
                clearInterval(checkInterval);

                const resizeObserver = new MutationObserver(() =>
                    applyChatStyles(iframe)
                );
                resizeObserver.observe(iframe, {
                    attributes: true,
                    attributeFilter: ['style'],
                });

                window.addEventListener('resize', () =>
                    applyChatStyles(iframe)
                );

                return () => {
                    resizeObserver.disconnect();
                    window.removeEventListener('resize', () =>
                        applyChatStyles(iframe)
                    );
                };
            }
        }, 300);

        return () => clearInterval(checkInterval);
    }, []);

    const toggleFormVisibility = () => {
        const form = document.getElementById('chat-web-container');
        const iframe = document.getElementById('sp-chat-frame');

        const isIframeVisible =
            iframe && getComputedStyle(iframe).display !== 'none';

        if (form) {
            form.style.display = isIframeVisible ? 'none' : 'block';
        }
    };

    useEffect(() => {
        const observer = new MutationObserver(() => {
            const minimizedWrapper =
                document.getElementById('sp-min-tab-wrapper');

            const isMinimized =
                minimizedWrapper &&
                getComputedStyle(minimizedWrapper).display !== 'none';
            window.parent.postMessage(
                {
                    type: 'chat-minimized-status',
                    minimized: isMinimized,
                },
                '*'
            );
            toggleFormVisibility();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class'],
        });

        return () => observer.disconnect();
    }, []);

    return (
        <>
            <ContainerWebChat>
                <Fragment>
                    <Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                                <Box mb={1}>
                                    <label className="crmware-previsora-chat-label">
                                        Nombres{' '}
                                        <b className="crmware-previsora-chat-label-required">
                                            *
                                        </b>
                                        :
                                    </label>
                                </Box>
                                <FieldText
                                    name="name"
                                    size="small"
                                    touched={formik.touched.name}
                                    errors={formik.errors.name}
                                    value={formik.values.name}
                                    onChange={(event) => {
                                        const { value } = event.target;
                                        if (/^[A-Za-z ]*$/.test(value)) {
                                            formik.handleChange(event);
                                        }
                                    }}
                                    onBlur={formik.handleBlur}
                                />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Box mb={1}>
                                    <label className="crmware-previsora-chat-label">
                                        Apellidos{' '}
                                        <b className="crmware-previsora-chat-label-required">
                                            *
                                        </b>
                                        :
                                    </label>
                                </Box>
                                <FieldText
                                    name="surname"
                                    size="small"
                                    touched={formik.touched.surname}
                                    errors={formik.errors.surname}
                                    value={formik.values.surname}
                                    onChange={(event) => {
                                        const { value } = event.target;
                                        if (/^[A-Za-z ]*$/.test(value)) {
                                            formik.handleChange(event);
                                        }
                                    }}
                                    onBlur={formik.handleBlur}
                                />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Box mb={1}>
                                    <label className="crmware-previsora-chat-label">
                                        Tipo de Identificación{' '}
                                        <b className="crmware-previsora-chat-label-required">
                                            *
                                        </b>
                                        :
                                    </label>
                                </Box>
                                <AutocompleteField
                                    data={formatParams(identifications)}
                                    size="small"
                                    name="type_identification"
                                    touched={formik.touched.type_identification}
                                    errors={formik.errors.type_identification}
                                    value={formik.values.type_identification}
                                    onChangeValue={(value: any) =>
                                        formik.setFieldValue(
                                            'type_identification',
                                            value
                                        )
                                    }
                                    onBlur={formik.handleBlur}
                                />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Box mb={1}>
                                    <label className="crmware-previsora-chat-label">
                                        Número de documento{' '}
                                        <b className="crmware-previsora-chat-label-required">
                                            *
                                        </b>
                                        :
                                    </label>
                                </Box>
                                <FieldText
                                    name="identification"
                                    size="small"
                                    touched={formik.touched.identification}
                                    errors={formik.errors.identification}
                                    value={formik.values.identification}
                                    onChange={(event) => {
                                        const { value } = event.target;
                                        if (
                                            /^\d*$/.test(value) ||
                                            value.includes('-')
                                        ) {
                                            formik.handleChange(event);
                                        }
                                    }}
                                    onBlur={formik.handleBlur}
                                />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Box mb={1}>
                                    <label className="crmware-previsora-chat-label">
                                        Correo{' '}
                                        <b className="crmware-previsora-chat-label-required">
                                            *
                                        </b>
                                        :
                                    </label>
                                </Box>
                                <FieldText
                                    name="email"
                                    size="small"
                                    touched={formik.touched.email}
                                    errors={formik.errors.email}
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                            </Grid>

                            <Grid item xs={12} md={12}>
                                <Box mb={1}>
                                    <label className="crmware-previsora-chat-label">
                                        Telefono{' '}
                                        <b className="crmware-previsora-chat-label-required">
                                            *
                                        </b>
                                        :
                                    </label>
                                </Box>
                                <FieldText
                                    name="phone"
                                    size="small"
                                    touched={formik.touched.phone}
                                    errors={formik.errors.phone}
                                    value={formik.values.phone}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                            </Grid>

                            <Grid item xs={12} md={12}>
                                <Box mb={1}>
                                    <label className="crmware-previsora-chat-label">
                                        Tipo de usuario{' '}
                                        <b className="crmware-previsora-chat-label-required">
                                            *
                                        </b>
                                        :
                                    </label>
                                </Box>
                                <AutocompleteField
                                    data={formatParams(contacts)}
                                    size="small"
                                    name="type_contact"
                                    touched={formik.touched.type_contact}
                                    errors={formik.errors.type_contact}
                                    value={formik.values.type_contact}
                                    onChangeValue={(value: any) =>
                                        formik.setFieldValue(
                                            'type_contact',
                                            value
                                        )
                                    }
                                    onBlur={formik.handleBlur}
                                />
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                display="flex"
                                justifyContent="center"
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            className="checkbox_boot"
                                            id="policy"
                                            name="aceptoPolitica"
                                            checked={formik.values.acceptPolicy}
                                            onChange={() => {
                                                formik.setFieldValue(
                                                    'acceptPolicy',
                                                    !formik.values.acceptPolicy
                                                );
                                            }}
                                            onBlur={formik.handleBlur}
                                        />
                                    }
                                    label={
                                        <label
                                            onClick={handleOpen}
                                            className="link-button"
                                        >
                                            Confirmo que conozco los términos,
                                            condiciones y acepto el tratamiento
                                            de datos personales.
                                        </label>
                                    }
                                />
                                {/* Modal */}
                                <ModalTermAndConditions
                                    handleClose={handleClose}
                                    open={open}
                                />
                            </Grid>

                            <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                <Button
                                    type="submit"
                                    className="crmware-previsora-chat-btn-primary"
                                    disabled={!isFormValid()}
                                    onClick={formik.handleSubmit as any}
                                >
                                    Iniciar Chat
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Fragment>
            </ContainerWebChat>
        </>
    );
};
